<div class="thread-details" [class.thread-details--embedded]="embedded" *ngIf="showThreadDetailsPopover" (click)="onStopPropagation($event)">
    
    <div class="details-header">
        <span>Thread Details</span>
        <small>Use ↑↓ to navigate, Esc to close</small>
    </div>
    
    <div class="details-body" *ngIf="currentThread">

        <div class="details-section details-section--first">
            <div class="details-section__title">Conversation name</div>

            <div class="thread-name-panel" *ngIf="!isEditingName; else editingThreadNameTemplate">
                <div class="thread-name-panel__copy">
                    <div class="thread-name-panel__value">{{ currentThread.name || 'Unnamed thread' }}</div>
                    <div class="thread-name-panel__hint">Rename the thread without leaving the conversation.</div>
                </div>

                <button type="button" class="thread-name-panel__button" (click)="startEditingName()">
                    <i class="fa-regular fa-pen-to-square"></i>
                    <span>Rename</span>
                </button>
            </div>

            <ng-template #editingThreadNameTemplate>
                <div class="thread-name-editor">
                    <input
                        type="text"
                        class="thread-name-editor__input"
                        [(ngModel)]="threadNameDraft"
                        placeholder="Conversation name"
                        maxlength="120"
                    />

                    <div class="thread-name-editor__actions">
                        <button type="button" class="thread-name-editor__button thread-name-editor__button--secondary" (click)="cancelEditingName($event)">
                            Cancel
                        </button>
                        <button type="button" class="thread-name-editor__button" (click)="saveThreadName($event)">
                            Save name
                        </button>
                    </div>
                </div>
            </ng-template>
        </div>
        
        <!-- Basic Thread Info -->
        <div class="thread-info">
            <div class="info-row focusable-item" 
                 [class.focused]="focusedItemIndex === 0"
                 tabindex="0">
                <span class="label">Agent:</span>
                <span class="value">{{ (currentThread.agentName || currentThread.agentType)?.toUpperCase() }}</span>
            </div>
            <div class="info-row focusable-item" 
                 [class.focused]="focusedItemIndex === 1"
                 tabindex="0">
                <span class="label">Status:</span>
                <span class="value">{{ statusDisplayName }}</span>
            </div>
            <div class="info-row focusable-item" 
                 [class.focused]="focusedItemIndex === 2"
                 tabindex="0">
                <span class="label">Age:</span>
                <span class="value">{{ threadAge }}</span>
            </div>
            <div class="info-row focusable-item" 
                 [class.focused]="focusedItemIndex === 3"
                 tabindex="0" 
                 *ngIf="currentThread.lastUsedModel">
                <span class="label">Model:</span>
                <span class="value">{{ getModelDisplayName(currentThread.lastUsedModel) }}</span>
            </div>
            <div class="info-row focusable-item" 
                 [class.focused]="focusedItemIndex === (currentThread.lastUsedModel ? 4 : 3)"
                 tabindex="0">
                <span class="label">Updated:</span>
                <span class="value">{{ threadUpdated }}</span>
            </div>
        </div>

        <div class="details-section context-window-section" *ngIf="hasContextUsage">
            <div class="details-section__title">Context window</div>
            <div class="context-window-card" [ngClass]="'context-window-card--' + contextUsageTone">
                <div class="context-window-card__header">
                    <div class="context-window-card__copy">
                        <strong>{{ contextFillPercent === null ? 'Unknown fullness' : contextFillPercent + '% full' }}</strong>
                        <span>{{ contextUsageModelLabel }}</span>
                    </div>
                    <span class="context-window-card__tokens">{{ contextUsageSummary }}</span>
                </div>

                <div class="context-window-card__track" aria-hidden="true">
                    <div class="context-window-card__bar" [style.width]="contextFillWidth"></div>
                </div>

                <div class="context-window-card__meta">{{ contextUsageSourceLabel }}</div>
            </div>
        </div>

        <div class="details-section">
            <div class="details-section__title">Conversation actions</div>
            <div class="details-action-list">
                <button type="button"
                        class="details-action details-action--danger focusable-item"
                        [class.focused]="focusedItemIndex === actionStartIndex"
                        (click)="onDeleteThreadSelected()">
                    <i class="fa-regular fa-trash-can"></i>
                    <span>Delete conversation and all messages</span>
                </button>
            </div>

            <div class="details-section__hint">
                Permanently removes this thread plus every message already recorded in it.
            </div>
        </div>
        
        <div class="token-usage" *ngIf="hasUsageSummary; else noUsageTemplate">
            <div class="usage-title">Credits and usage</div>
            
            <div class="usage-stats">
                <div class="stat-row focusable-item" 
                     [class.focused]="focusedItemIndex === tokenUsageStartIndex"
                     tabindex="0">
                    <span class="stat-label">Total Credits</span>
                    <span class="stat-value">{{ formatCredits(usageTotalCredits) }}</span>
                </div>
                <div class="stat-row focusable-item" 
                     [class.focused]="focusedItemIndex === tokenUsageStartIndex + 1"
                     tabindex="0">
                    <span class="stat-label">Model Credits</span>
                    <span class="stat-value">{{ formatCredits(usageModelCredits) }}</span>
                </div>
                <div class="stat-row focusable-item" 
                     [class.focused]="focusedItemIndex === tokenUsageStartIndex + 2"
                     tabindex="0">
                    <span class="stat-label">Compute Credits</span>
                    <span class="stat-value">{{ formatCredits(usageComputeCredits) }}</span>
                </div>
                <div class="stat-row focusable-item" 
                     [class.focused]="focusedItemIndex === tokenUsageStartIndex + 3"
                     tabindex="0">
                    <span class="stat-label">Total Tokens</span>
                    <span class="stat-value">{{ formatTokenCount(usageTotalTokens) }}</span>
                </div>
                <div class="stat-row focusable-item" 
                     [class.focused]="focusedItemIndex === tokenUsageStartIndex + 4"
                     tabindex="0">
                    <span class="stat-label">Total Compute</span>
                    <span class="stat-value">{{ formatDuration(usageTotalComputeSeconds, usageTotalComputeSeconds * 1000) }}</span>
                </div>
            </div>

            <div class="usage-substats">
                <div class="usage-chip">
                    <span>Input</span>
                    <strong>{{ formatTokenCount(usageTotalInputTokens) }}</strong>
                </div>
                <div class="usage-chip">
                    <span>Output</span>
                    <strong>{{ formatTokenCount(usageTotalOutputTokens) }}</strong>
                </div>
                <div class="usage-chip" *ngIf="hasTokenUsage">
                    <span>API Calls</span>
                    <strong>{{ tokenUsage!.apiCallCount }}</strong>
                </div>
                <div class="usage-chip" *ngIf="hasCreditEvents">
                    <span>Iterations</span>
                    <strong>{{ usageIterationsCount }}</strong>
                </div>
            </div>
            
            <div class="cost-info">
                <div class="cost-copy">
                    <span class="cost-label">{{ hasCreditEvents ? 'Recorded USD cost' : 'Token-only estimate' }}</span>
                    <small *ngIf="hasCreditEvents; else noRecordedCostCopy">
                        {{ usageIterationsCount }} recorded {{ usageIterationsCount === 1 ? 'iteration' : 'iterations' }} mapped to this thread.
                    </small>
                    <ng-template #noRecordedCostCopy>
                        <small *ngIf="hasMessageCostRows; else noCostRowsCopy">
                            Estimated from message token usage only.
                        </small>
                    </ng-template>
                    <ng-template #noCostRowsCopy>
                        <small>No credit events or per-message token cost records have been captured yet.</small>
                    </ng-template>
                </div>
                <span class="cost-value" *ngIf="hasCreditEvents; else estimatedCostValue">{{ formatUsdCost(usageTotalCostUsd) }}</span>
                <ng-template #estimatedCostValue>
                    <span class="cost-value" *ngIf="hasCalculatedMessageCost; else unavailableCost">{{ formatCost(calculatedMessageCostTotal, calculatedMessageCostCurrency) }}</span>
                </ng-template>
                <ng-template #unavailableCost>
                    <span class="cost-value">Unavailable</span>
                </ng-template>
            </div>

            <div class="message-costs" *ngIf="hasCreditEvents; else fallbackMessageCostsTemplate">
                <div class="message-costs-header">
                    <span>Per-Iteration Credit Breakdown</span>
                    <small>Credits are recorded once per agent iteration, then broken down here into tokens, compute time, and total charge.</small>
                </div>

                <div class="message-cost-table-shell">
                    <table class="message-cost-table">
                        <thead>
                            <tr>
                                <th>Iteration</th>
                                <th>Model</th>
                                <th>Credits</th>
                                <th>Tokens</th>
                                <th>Compute</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let row of threadCreditEventRows">
                                <td>
                                    <div class="message-cost-primary">#{{ row.iteration }}</div>
                                    <div class="message-cost-secondary">{{ row.createdAtLabel }}</div>
                                </td>
                                <td>
                                    <div class="message-cost-primary">{{ row.modelDisplayName }}</div>
                                    <div class="message-cost-secondary" *ngIf="row.modelKey">{{ row.modelKey }}</div>
                                </td>
                                <td>
                                    <div class="message-cost-primary">{{ formatCredits(row.totalCredits) }}</div>
                                    <div class="message-cost-secondary">Model {{ formatCredits(row.modelCredits) }} · Compute {{ formatCredits(row.computeCredits) }}</div>
                                </td>
                                <td>
                                    <div class="message-cost-primary">{{ formatTokenCount(row.totalTokens) }}</div>
                                    <div class="message-cost-secondary">In {{ formatTokenCount(row.promptTokens) }} · Out {{ formatTokenCount(row.completionTokens) }}</div>
                                </td>
                                <td>
                                    <div class="message-cost-primary">{{ formatDuration(row.computeSeconds, row.computeMilliseconds) }}</div>
                                    <div class="message-cost-secondary">Total {{ formatUsdCost(row.totalCostUsd) }} · Compute {{ formatUsdCost(row.computeCostUsd) }}</div>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="2">Recorded Total</td>
                                <td>{{ formatCredits(usageTotalCredits) }}</td>
                                <td>{{ formatTokenCount(usageTotalTokens) }}</td>
                                <td>{{ formatDuration(usageTotalComputeSeconds, usageTotalComputeSeconds * 1000) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>

        <ng-template #fallbackMessageCostsTemplate>
            <div class="message-costs" *ngIf="hasMessageCostRows; else noMessageCostRows">
                <div class="message-costs-header">
                    <span>Estimated Message Cost Breakdown</span>
                    <small>Calculated from each message's recorded model and token usage because no credit events were found for this thread yet.</small>
                </div>

                <div class="message-cost-table-shell">
                    <table class="message-cost-table">
                        <thead>
                            <tr>
                                <th>Message</th>
                                <th>Model</th>
                                <th>Tokens</th>
                                <th>Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let row of messageCostRows">
                                <td>
                                    <div class="message-cost-primary">#{{ row.messageNumber }} · {{ row.roleLabel }}</div>
                                    <div class="message-cost-secondary">{{ row.timestampLabel }}</div>
                                </td>
                                <td>
                                    <div class="message-cost-primary">{{ row.modelDisplayName }}</div>
                                    <div class="message-cost-secondary">{{ row.modelKey }}</div>
                                </td>
                                <td>
                                    <div class="message-cost-primary">{{ formatTokenCount(row.totalTokens) }}</div>
                                    <div class="message-cost-secondary">In {{ formatTokenCount(row.promptTokens) }} · Out {{ formatTokenCount(row.completionTokens) }}</div>
                                </td>
                                <td>
                                    <div class="message-cost-primary" [class.message-cost-primary--unavailable]="row.totalCost === null">{{ formatRowCost(row.totalCost, row.currency) }}</div>
                                    <div class="message-cost-secondary" *ngIf="row.totalCost !== null">In {{ formatRowCost(row.inputCost, row.currency) }} · Out {{ formatRowCost(row.outputCost, row.currency) }}</div>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot *ngIf="hasCalculatedMessageCost">
                            <tr>
                                <td colspan="3">Estimated Total</td>
                                <td>{{ formatCost(calculatedMessageCostTotal, calculatedMessageCostCurrency) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </ng-template>

        <ng-template #noMessageCostRows>
            <div class="no-usage no-usage--compact">
                <p>No credit events or message-level token usage have been recorded for this thread yet.</p>
            </div>
        </ng-template>

        <ng-template #noUsageTemplate>
            <div class="no-usage">
                <p>No credit or token usage data available yet.</p>
            </div>
        </ng-template>
        
    </div>
    
    <!-- No Thread -->
    <div class="no-thread" *ngIf="!currentThread">
        <p>No thread selected</p>
    </div>
    
</div>
